"use client";

import { useCallback, useContext, useEffect, useState } from "react";
import { DealershipContext } from "./dealership-context";
import Cookies from "js-cookie";
import { UserContext } from "./user-context";
import { featureFlags } from "../consts";
import {
  User,
  UserGroup,
  OrganizedUserGroups,
} from "@/app/types/sale-log-types";
import { getUsersForDealer } from "@/lib/actions/get-users-dealership";
import { getUsersGroupsForDealer } from "@/lib/actions/get-user-groups";
import { sortUsersIntoDynamicGroups } from "@/lib/utils";
import { DealershipConfiguration, TSFixMe } from "@/app/types/types";

export type Dealership = {
  name: string;
  key: string;
  id: string;
  _id?: string;
  active: boolean;
  dealershipConfiguration: DealershipConfiguration;
  featureOverrides?: Record<string, boolean>;
  dealershipLogo?: string;
};

export const DealershipContextWrapper = ({ children }) => {
  const { user, isLoading, getUserGroups, setUserGroups, userGroups } =
    useContext(UserContext);
  const [userDealerships, setUserDealerships] = useState<Dealership[]>([]);
  const [currentDealership, setCurrentDealership] = useState<Dealership>();
  const [loading, setLoading] = useState(true);
  const [dealershipsError, setDealershipsError] = useState<
    "no-access" | "fetch-failed" | null
  >(null);

  // User management state
  const [users, setUsers] = useState<User[]>([]);
  const [dealershipUserGroups, setDealershipUserGroups] = useState<UserGroup[]>(
    [],
  );
  const [sortedUserGroups, setSortedUserGroups] = useState<OrganizedUserGroups>(
    {},
  );
  const [salesPeople, setSalesPeople] = useState<string[]>([]);
  const [salesManagers, setSalesManagers] = useState<string[]>([]);
  const [financeManagers, setFinanceManagers] = useState<string[]>([]);
  const [serviceAdvisors, setServiceAdvisors] = useState<string[]>([]);
  const [partsAdvisors, setPartsAdvisors] = useState<string[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      return;
    }
    const fetchUserDealerships = async () => {
      // Every path out of here must either produce a dealership or set an
      // error: `loading` is only cleared once a dealership resolves, so a
      // silent bail leaves the app on the loading skeleton indefinitely.
      let response: Response;
      try {
        response = await fetch(`/v2/api/user/${user.id}/dealerships`);
      } catch (e) {
        console.error("Failed to fetch dealerships", e);
        setDealershipsError("fetch-failed");
        setLoading(false);
        return;
      }

      if (!response.ok) {
        console.error("Failed to fetch dealerships", response.status);
        setDealershipsError("fetch-failed");
        setLoading(false);
        return;
      }

      const data: TSFixMe[] = await response.json();

      // An empty list is a 200, not an error — the account exists but has no
      // user_dealerships row, so there is nothing to load and never will be
      // until an admin links them. Say so rather than spinning.
      if (data.length === 0) {
        console.error(`User ${user.id} has no dealership access`);
        setDealershipsError("no-access");
        setLoading(false);
        return;
      }

      setDealershipsError(null);
      setUserDealerships(
        data.map((dealership) => ({
          name: dealership.name,
          key: dealership.key,
          id: dealership._id,
          active: dealership.active,
          dealershipConfiguration: dealership.dealershipConfiguration,
          featureOverrides: dealership.featureOverrides,
          dealershipLogo: dealership.dealershipLogo,
        })),
      );
    };
    fetchUserDealerships();
  }, [user]);

  const cachedDealershipId = Cookies.get("dealershipId");

  useEffect(() => {
    if (!userDealerships || userDealerships.length < 1) return;

    const found = userDealerships.find(
      (dealership) => dealership.id === cachedDealershipId,
    );

    if (!found) {
      Cookies.remove("dealershipId");
      setCurrentDealership(userDealerships[0]);
      Cookies.set("dealershipId", userDealerships[0].id);
      return;
    }
    setCurrentDealership(found);
  }, [userDealerships, cachedDealershipId]);

  useEffect(() => {
    const setupUser = async () => {
      if (!user || !currentDealership) return;

      try {
        const userGroups = await getUserGroups(
          currentDealership._id ?? currentDealership.id,
        );

        setUserGroups(userGroups);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    setupUser();
  }, [currentDealership, user, getUserGroups, setUserGroups]);

  useEffect(() => {
    if (currentDealership && userGroups) {
      setLoading(false);
    }
  }, [currentDealership, userGroups]);

  // User management functions
  const fetchAvailableUsers = useCallback(async (): Promise<User[]> => {
    if (!currentDealership) return [];
    try {
      const response = await getUsersForDealer({ id: currentDealership.id });
      return response || [];
    } catch (error) {
      console.error("fetchAvailableUsers failed:", (error as Error).message);
      throw error;
    }
  }, [currentDealership]);

  const fetchDealershipUserGroups = useCallback(async (): Promise<
    UserGroup[]
  > => {
    if (!currentDealership) return [];
    try {
      const response = await getUsersGroupsForDealer({
        id: currentDealership.id,
      });
      return response || [];
    } catch (error) {
      console.error("fetchUserGroups failed:", (error as Error).message);
      throw error;
    }
  }, [currentDealership]);

  const refetchUsers = useCallback(async () => {
    if (!currentDealership) return;

    const userSession = Cookies.get("__session");
    if (!userSession) return;

    setUsersLoading(true);
    setUsersError(null);

    try {
      const [usersData, userGroupsData] = await Promise.all([
        fetchAvailableUsers(),
        fetchDealershipUserGroups(),
      ]);

      setUsers(usersData);
      setDealershipUserGroups(userGroupsData);

      if (usersData && userGroupsData) {
        const sortedUserGroupsNew = sortUsersIntoDynamicGroups(
          currentDealership.id,
          currentDealership.dealershipConfiguration.houseDealsEnabled,
          usersData,
          userGroupsData,
        );

        setSortedUserGroups(sortedUserGroupsNew);

        // Extract convenience arrays
        setSalesPeople(
          sortedUserGroupsNew["Sales Person"]?.map((u) => u.name) || [],
        );
        setSalesManagers(
          sortedUserGroupsNew["Sales Manager"]?.map((u) => u.name) || [],
        );
        setFinanceManagers(
          sortedUserGroupsNew["Financial Services Manager"]?.map(
            (u) => u.name,
          ) || [],
        );
        setServiceAdvisors(
          sortedUserGroupsNew["Service Advisor"]?.map((u) => u.name) || [],
        );
        setPartsAdvisors(
          sortedUserGroupsNew["Parts Advisor / Other"]?.map((u) => u.name) ||
            [],
        );
      }
    } catch (error) {
      console.error(
        "Error fetching dealership users data:",
        (error as Error).message,
      );
      setUsersError((error as Error).message);
    } finally {
      setUsersLoading(false);
    }
  }, [currentDealership, fetchAvailableUsers, fetchDealershipUserGroups]);

  // Fetch users when dealership changes
  useEffect(() => {
    refetchUsers();
  }, [refetchUsers]);

  const hasFeature = useCallback(
    (feature: string) => {
      if (!currentDealership) return false;

      // `??` not `||`: an explicit per-dealership `false` must beat a globally-on
      // default, otherwise a flag can only ever be switched on, never off.
      return (
        currentDealership.featureOverrides?.[feature] ??
        featureFlags[feature] ??
        false
      );
    },
    [currentDealership],
  );

  const ctx: DealershipContext = {
    currentDealership,
    setCurrentDealership: (dealership) => {
      Cookies.set("dealershipId", dealership.id);
    },
    dealerships: userDealerships,
    setDealerships: setUserDealerships,
    hasFeature,
    loading,
    dealershipsError,

    // User management
    users,
    userGroups: dealershipUserGroups,
    sortedUserGroups,
    salesPeople,
    salesManagers,
    financeManagers,
    serviceAdvisors,
    partsAdvisors,
    usersLoading,
    usersError,
    refetchUsers,
  };

  return (
    <DealershipContext.Provider value={ctx}>
      {children}
    </DealershipContext.Provider>
  );
};
